import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    FunctionField,
    useTranslate,
    useRecordContext,
    useRedirect,
    EditButton,
    DeleteButton,
    TopToolbar,
    ListButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
} from '@mui/material';
import { ArrowBack, Groups } from '@mui/icons-material';
import { MembersTab } from './MembersTab';

const ShowActions = () => (
    <TopToolbar>
        <ListButton />
        <EditButton />
        <DeleteButton />
    </TopToolbar>
);

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    if (!record) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/user-groups')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.user-groups.list.title')}
            >
                <ArrowBack />
            </IconButton>
            <Groups sx={{ color: '#009A75', fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                    {record.name}
                </Typography>
                {record.description && (
                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} color="text.secondary">
                        {record.description}
                    </Typography>
                )}
            </Box>
            <Chip
                label={`${record.memberCount ?? 0} ${translate('resources.user-groups.fields.memberCount')}`}
                color="primary"
                variant="outlined"
            />
        </Box>
    );
};

const DetailsCard = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.user-groups.sections.basic')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">{translate('resources.user-groups.fields.id')}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{record.id}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">{translate('resources.user-groups.fields.createdAt')}</Typography>
                        <Typography variant="body2">{record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">{translate('resources.user-groups.fields.updatedAt')}</Typography>
                        <Typography variant="body2">{record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}</Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const MembersCard = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <MembersTab groupId={String(record.id)} />;
};

export const UserGroupShow = () => (
    <Show component="div" title={false} actions={<ShowActions />}>
        <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', p: { xs: 1, sm: 2 } }}>
            <PageHeader />
            <DetailsCard />
            <MembersCard />
        </Box>
    </Show>
);

export default UserGroupShow;
